import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Legate Consulting',
  description = 'Legate Business Consulting - Entrepreneurship Is Our Freedom. Professional business consulting services to help you achieve your entrepreneurial goals.',
  keywords = 'business consulting, entrepreneurship, business strategy, startup consulting, business development, Legate Consulting',
  url = 'https://legate-llp.com',
  image = 'https://legate-llp.com/og-image.jpg',
  type = 'website',
  author = 'Legate Consulting',
  twitterHandle = '@LegateLLP'
}) => {
  const siteTitle = title === 'Legate Consulting' ? title : `${title} | Legate Consulting`;
  const canonicalUrl = url;

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Legate Consulting" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="General" />
      
      {/* Mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Favicon and app icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/manifest.json" />
      <meta name="theme-color" content="#000000" />
      
      {/* Preload critical resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Legate Consulting",
          "alternateName": "Legate LLP",
          "description": "Professional business consulting services to help entrepreneurs achieve their goals",
          "url": "https://legate-llp.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://legate-llp.com/logo.png",
            "width": "400",
            "height": "400"
          },
          "foundingDate": "2023",
          "slogan": "Entrepreneurship Is Our Freedom",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["English"]
          },
          "sameAs": [
            "https://linkedin.com/company/legate-llp",
            "https://twitter.com/LegateLLP"
          ],
          "areaServed": "US",
          "serviceType": [
            "Business Formation",
            "LLC Services",
            "Corporation Setup",
            "Business Compliance",
            "Registered Agent Services"
          ],
          "knowsAbout": [
            "Business Formation",
            "Entrepreneurship",
            "Business Compliance",
            "Corporate Law",
            "LLC Formation",
            "Corporation Setup"
          ]
        })}
      </script>
      
      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Legate Consulting",
          "url": "https://legate-llp.com",
          "description": description,
          "publisher": {
            "@type": "Organization",
            "name": "Legate Consulting"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://legate-llp.com/search?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;